import React, { KeyboardEvent } from 'react';

import { Box, Tab, Tabs, Typography } from '@material-ui/core';

import { TabCountLabel } from '@vyce/core/src/components/TabCountLabel';

import { InviteModuleConfig } from '../constants';
import { InviteSelectionApi } from '../hooks/useInviteSelection';
import useStyles from '../styles';
import { BulkPhoneEntryApi, ResourceOption, WorkersFilter } from '../types';
import { ExistingUsersPanel } from './ExistingUsersPanel';
import { NewUsersPanel } from './NewUsersPanel';

interface Props {
  tab: number;
  onTabChange: (tab: number) => void;
  newCount: number;
  existingCount: number;
  // Invite new users
  phones: string[];
  addPhoneToList: () => void;
  handlePhoneFieldKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  deleteContact: (phone: string) => void;
  contactsDisabled: boolean;
  bulkPaste: BulkPhoneEntryApi;
  // Existing Vyce users
  companyId: string;
  filter: WorkersFilter;
  onFilterChange: (filter: WorkersFilter) => void;
  filterModules: InviteModuleConfig[];
  resourceOptions: ResourceOption[];
  selection: InviteSelectionApi;
  // Blockers surfaced before the user reaches step 2
  noModulesAvailable: boolean;
  limitError: string | null;
}

export const WhoStep: React.FC<Props> = ({
  tab,
  onTabChange,
  newCount,
  existingCount,
  phones,
  addPhoneToList,
  handlePhoneFieldKeyDown,
  deleteContact,
  contactsDisabled,
  bulkPaste,
  companyId,
  filter,
  onFilterChange,
  filterModules,
  resourceOptions,
  selection,
  noModulesAvailable,
  limitError,
}) => {
  const classes = useStyles();

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <Tabs
        className={classes.tabs}
        value={tab}
        onChange={(_, value) => onTabChange(value)}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab
          disableRipple
          className={classes.tab}
          label={<TabCountLabel title="Invite new users" count={newCount} active={tab === 0} />}
        />
        <Tab
          disableRipple
          className={classes.tab}
          label={<TabCountLabel title="Existing Vyce users" count={existingCount} active={tab === 1} />}
        />
      </Tabs>

      {noModulesAvailable && (
        <Typography variant="body2" className={classes.errorText}>
          There's no module you can add people to yet. Activate Teams, Time &amp; Attendance or Payroll, or
          ask an admin for access.
        </Typography>
      )}
      {limitError && (
        <Typography variant="body2" className={classes.errorText}>
          {limitError}
        </Typography>
      )}

      {/* Both panels stay mounted so the phone input, search and scroll survive tab switching */}
      <Box hidden={tab !== 0}>
        <NewUsersPanel
          phones={phones}
          addPhoneToList={addPhoneToList}
          handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
          deleteContact={deleteContact}
          disabled={contactsDisabled}
          bulkPaste={bulkPaste}
        />
      </Box>

      <Box hidden={tab !== 1}>
        <ExistingUsersPanel
          companyId={companyId}
          filter={filter}
          onFilterChange={onFilterChange}
          filterModules={filterModules}
          resourceOptions={resourceOptions}
          selection={selection}
        />
      </Box>
    </Box>
  );
};
